/**
 * Hub — six themed cards.
 *
 * Composition (touch-TV contract, docs/TOUCH-TV-CONTRACT.md):
 *   - masthead row: gold hairline rule + WORD SEARCH eyebrow, a large
 *     display title and an italic lede at the left; the Daily/Practice
 *     mode toggle sits to its right on landscape.
 *   - 3×2 grid of large cards with generated atmosphere imagery and a
 *     bottom-anchored gradient scrim, absorbing the remaining height —
 *     ONE complete screenful at every landscape viewport ≥1024px.
 *   - hairline rule + a single closing line at the bottom — no void.
 *
 * Phone portrait stacks and scrolls (personal, held devices).
 */
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { AtmosphereSurface, buildSrcSet, variantOf } from '@/juice/RevealedImage';
import { usePreferences } from '@/preferences';

import { hoverSpring, shelfSpring } from './motion';
import { paletteFor } from './palette';
import { sound } from './sound';
import { atmosphereSrc, THEME_SURFACES, type ThemeSurface } from './themes';

import './Hub.css';

type WordSearchMode = 'daily' | 'practice';

interface ModeMeta {
  slug: WordSearchMode;
  title: string;
  blurb: string;
}

const MODES: ReadonlyArray<ModeMeta> = [
  {
    slug: 'daily',
    title: "Today's Puzzles",
    blurb: 'A shared daily puzzle for each theme.',
  },
  {
    slug: 'practice',
    title: 'Practice',
    blurb: 'Fresh themed puzzles whenever you want another.',
  },
];

export function Hub() {
  const { theme } = usePreferences();
  const [selectedMode, setSelectedMode] = useState<WordSearchMode>('daily');

  // Pre-warm the audio graph so the first chime doesn't hiccup.
  useEffect(() => {
    sound.unlock();
  }, []);

  // NB: we deliberately do NOT gate render on the host `init` handshake.
  // Nothing on this surface needs host data yet (resident, leaderboards, …),
  // and blocking here just broke embedded demos. See host/types.ts.

  return (
    <div className="ts-hub" data-theme={theme}>
      <div className="ts-hub__paper" aria-hidden="true" />
      <div className="ts-hub__paper-noise" aria-hidden="true" />

      <div className="ts-hub__inner">
        <header className="ts-hub__masthead">
          <p className="ts-hub__eyebrow" aria-hidden="true">
            <span className="ts-hub__eyebrow-rule" />
            <span className="ts-hub__eyebrow-text">Word Search</span>
          </p>
          <motion.h1
            className="ts-hub__title"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.25, 1, 0.5, 1] }}
          >
            Pick a <em>theme</em>.
          </motion.h1>
          <motion.p
            className="ts-hub__lede"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08, ease: [0.25, 1, 0.5, 1] }}
          >
            Choose today&rsquo;s shared theme puzzle or a fresh practice round. No clock, no score
            &mdash; just a quiet half-hour.
          </motion.p>
        </header>

        <section className="ts-hub__mode" aria-labelledby="ts-hub-mode-title">
          <h2 id="ts-hub-mode-title" className="sr-only">
            Word Search mode
          </h2>
          <div className="ts-hub__mode-toggle" role="group" aria-label="Word Search mode">
            {MODES.map((mode) => (
              <button
                key={mode.slug}
                type="button"
                className="ts-mode"
                data-active={selectedMode === mode.slug ? 'true' : 'false'}
                onPointerDown={() => sound.unlock()}
                onClick={() => {
                  sound.play('letterTap');
                  setSelectedMode(mode.slug);
                }}
                aria-pressed={selectedMode === mode.slug}
              >
                <span className="ts-mode__title">{mode.title}</span>
                <span className="ts-mode__blurb">{mode.blurb}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="ts-hub__shelf" aria-labelledby="ts-hub-shelf-title">
          <h2 id="ts-hub-shelf-title" className="sr-only">
            Themed puzzles
          </h2>
          <ul className="ts-hub__cards" role="list">
            {THEME_SURFACES.map((surface, i) => (
              <motion.li
                key={surface.slug}
                className="ts-hub__card-wrap"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...shelfSpring, delay: 0.12 + i * 0.05 }}
                whileHover={{ y: -4, transition: hoverSpring }}
                whileTap={{ scale: 0.985, transition: { duration: 0.08 } }}
              >
                <ShelfCard surface={surface} themeMode={theme} mode={selectedMode} index={i} />
              </motion.li>
            ))}
          </ul>
        </section>

        <footer className="ts-hub__footer" aria-hidden="true">
          <span className="ts-hub__footer-rule" />
          <span className="ts-hub__footer-text">More categories coming.</span>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shelf card — atmosphere image, scrim, title + tagline + START.
// ---------------------------------------------------------------------------

function ShelfCard({
  surface,
  themeMode,
  mode,
  index,
}: {
  surface: ThemeSurface;
  themeMode: 'light' | 'dark';
  mode: WordSearchMode;
  index: number;
}): React.JSX.Element {
  const p = paletteFor(surface.slug, themeMode);
  const imgSrc = atmosphereSrc(surface.slug, themeMode);
  // All six cards share one screenful on landscape — eager-load them all.
  // (index kept for potential stagger tuning.)
  void index;
  const eager = true;
  return (
    <Link
      to={`/play/word-search?category=${surface.slug}&mode=${mode}`}
      className="ts-card"
      data-theme={themeMode}
      data-slug={surface.slug}
      style={
        {
          ['--th-paper' as string]: p.paper,
          ['--th-paper-warm' as string]: p.paperWarm,
          ['--th-paper-deep' as string]: p.paperDeep,
          ['--th-ink' as string]: p.ink,
          ['--th-ink-soft' as string]: p.inkSoft,
          ['--th-accent' as string]: p.accent,
          ['--th-glow' as string]: p.glow,
        } as React.CSSProperties
      }
      onPointerDown={() => sound.unlock()}
      onClick={() => sound.play('cardEnter')}
      aria-label={`Play ${mode === 'daily' ? "today's" : 'a practice'} ${surface.title} word search`}
    >
      <div className="ts-card__image" aria-hidden="true">
        <AtmosphereSurface
          src={imgSrc}
          avifSrc={variantOf(imgSrc, 'avif')}
          webpSrc={variantOf(imgSrc, 'webp')}
          // Canonicals are 1024×1024; pipeline elides 1366w. Use only the
          // widths that exist on disk so the browser never picks a missing
          // file (which Vite's SPA fallback serves as 200/index.html →
          // image decodes as 0×0). See Game.tsx for full notes.
          avifSrcSet={buildSrcSet(imgSrc, 'avif', [430, 860])}
          webpSrcSet={buildSrcSet(imgSrc, 'webp', [430, 860])}
          sizes="(max-width: 430px) 100vw, (max-width: 1024px) 50vw, 33vw"
          eager={eager}
        />
      </div>
      <div className="ts-card__scrim" aria-hidden="true" />

      <div className="ts-card__body">
        <p className="ts-card__mode">{mode === 'daily' ? 'Daily' : 'Practice'}</p>
        <h3 className="ts-card__title">{surface.title}</h3>
        <p className="ts-card__tag">{surface.tagline}</p>
      </div>

      <div className="ts-card__cta" aria-hidden="true">
        <span className="ts-card__cta-text">Start</span>
        <span className="ts-card__cta-arrow">&rarr;</span>
      </div>
    </Link>
  );
}
